import { Button } from "@bcc/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bcc/components/ui/dialog";
import { Input } from "@bcc/components/ui/input";
import type { FormProduct } from "@shared/types";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { setSsBagSizeProductSearchModalTestId } from "./ss-bag-size-product-search-modal.utils";

interface SsBagSizeProductSearchModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onProductSelected: (product: FormProduct) => void;
  readonly products: FormProduct[];
  readonly testId?: string;
}

export function SsBagSizeProductSearchModal({ open, onOpenChange, onProductSelected, products, testId }: SsBagSizeProductSearchModalProps) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");

  const filteredProducts = useMemo(() => {
    if (!searchValue.trim()) return products;
    const query = searchValue.toLowerCase();
    return products.filter((product) => product.title?.toLowerCase().includes(query) || product.id?.toLowerCase().includes(query));
  }, [products, searchValue]);

  const handleSelect = (product: FormProduct) => {
    setSearchValue("");
    onProductSelected(product);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSearchValue("");
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        testId={testId ?? setSsBagSizeProductSearchModalTestId("content")}
        className="sm:max-w-[720px] max-h-[80vh] flex flex-col"
      >
        <DialogHeader>
          <DialogTitle data-testid={setSsBagSizeProductSearchModalTestId("title")}>{t("ssRoaster.newBagSize.title")}</DialogTitle>
          <DialogDescription>{t("ssRoaster.newBagSize.description")}</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={t("ssRoaster.newBagSize.searchPlaceholder")}
            data-testid={setSsBagSizeProductSearchModalTestId("search")}
          />
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 max-h-[50vh] space-y-2 py-2">
          {filteredProducts.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">{t("ssRoaster.newBagSize.noResults")}</p>
          ) : (
            filteredProducts.map((product) => (
              <div
                key={product.id}
                className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-muted/50"
                data-testid={setSsBagSizeProductSearchModalTestId(`product-${product.id}`)}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{product.title ?? t("ssRoaster.newBagSize.untitledProduct")}</p>
                  <p className="text-sm text-muted-foreground">
                    {product.productType}
                    {product.variants?.length
                      ? ` \u2022 ${t("ssRoaster.newBagSize.variants", { count: product.variants.length }) as string}`
                      : ""}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSelect(product)}
                  data-testid={setSsBagSizeProductSearchModalTestId(`select-${product.id}`)}
                >
                  {t("ssRoaster.newBagSize.select")}
                </Button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
